import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./auth/AuthProvider";
import { StoreProvider } from "./lib/store";
import { Layout } from "./components/Shell";
import { LoginPage } from "./pages/Login";
import { MfaSetupPage } from "./auth/MfaSetup";
import { DashboardPage } from "./pages/Dashboard";
import { LibraryPage } from "./pages/Library";
import { DocumentPage } from "./pages/DocumentPage";
import { EditorPage } from "./pages/EditorPage";
import { ClientsPage } from "./pages/clienthub/Clients";
import { ClientPage } from "./pages/clienthub/Client";
import { M365Approved } from "./pages/clienthub/M365";
import { AgreementsPage } from "./pages/clienthub/Agreements";
import { AgreementTypesPage } from "./pages/settings/AgreementTypes";
import { NewDocumentPage } from "./pages/NewDocument";
import { ApprovalsPage, DraftsPage } from "./pages/Queues";
import { HelpHome, HelpArticlePage } from "./pages/help/Help";
import { AcronymsPage } from "./pages/Acronyms";
import { MembersPage } from "./pages/Members";
import { CompaniesPage } from "./pages/Companies";
import { CodeListsPage } from "./pages/CodeLists";
import { PublishKeysPage } from "./pages/PublishKeys";
import { ProfilePage } from "./pages/Profile";
import { AuditPage } from "./pages/Audit";
import { AuthCallbackPage } from "./pages/AuthCallback";
import { ImportPage } from "./pages/Import";
import { SettingsLayout } from "./pages/settings/SettingsLayout";
import { ApprovalsPage as ApprovalSettingsPage } from "./pages/settings/Approvals";
import { ClientHubAccessPage } from "./pages/settings/ClientHubAccess";
import { ReviewHubAccessPage, RatingDefinitionsPage, RemindersPage, ClientPageSectionsPage } from "./pages/settings/ReviewHubSettings";
import { ReviewsPage } from "./pages/reviewhub/Reviews";
import { ReviewPage } from "./pages/reviewhub/Review";
import { ServicesSettingsPage } from "./pages/settings/Services";
import { TasksPage } from "./pages/Tasks";
import { WelcomePage } from "./pages/Welcome";
import { IntegrationsPage } from "./pages/settings/Integrations";
import { ChecklistTemplatesPage } from "./pages/settings/ChecklistTemplates";
import { WorkflowTemplatesPage } from "./pages/settings/WorkflowTemplates";
import { WorkflowsPage } from "./pages/workflows/Workflows";
import { VendorsPage } from "./pages/vendorhub/Vendors";
import { VendorPage } from "./pages/vendorhub/Vendor";
import { VendorHubAccessPage, VendorRulesPage } from "./pages/settings/VendorHubSettings";
import { WorkflowPage } from "./pages/workflows/Workflow";
import { AiSettingsPage } from "./pages/settings/AiSettings";
import dvEndorsed from "./assets/brand/dv-endorsed.svg";

function Boot({ children }: { children: React.ReactNode }) {
  return (
    <div className="boot">
      <div style={{ textAlign: "center" }}>
        <img src={dvEndorsed} alt="DocVault from ITAssurance" />
        {children}
      </div>
    </div>
  );
}

/** Redirect an old address, keeping ?co= and ?tab=. */
function KeepQuery({ to }: { to: string }) {
  const loc = useLocation();
  return <Navigate to={to + loc.search} replace />;
}

export function App() {
  const { me, loading, loadError, refresh } = useAuth();
  const loc = useLocation();

  // Microsoft sign-in lands here whether or not a session exists yet.
  if (loc.pathname === "/auth/callback") return <AuthCallbackPage />;
  if (loading) return <Boot><div className="spin">Opening DocVault…</div></Boot>;

  if (!me) {
    if (loadError && loc.pathname !== "/login") {
      return (
        <Boot>
          <p style={{ maxWidth: 380 }}><b style={{ color: "var(--ink)" }}>DocVault can't be reached right now.</b><br />{loadError}</p>
          <button className="btn btn-accent" onClick={() => void refresh()}>Try again</button>
        </Boot>
      );
    }
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace state={{ from: loc.pathname }} />} />
      </Routes>
    );
  }

  // Two-step sign-in is required for password sign-ins (2026-10-27): nothing opens until it's on.
  if (me.mfa?.required && !me.mfa.on) return <MfaSetupPage />;

  return (
    <StoreProvider me={me} key={me.user.id}>
      <Routes>
        <Route path="/login" element={<Navigate to={(loc.state as { from?: string } | null)?.from ?? "/"} replace />} />
        <Route element={<Layout />}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/doc/:id" element={<DocumentPage />} />
          <Route path="/doc/:id/edit" element={<EditorPage />} />
          <Route path="/clienthub" element={<ClientsPage />} />
          <Route path="/clienthub/c/:id" element={<ClientPage />} />
          <Route path="/m365/approved" element={<M365Approved />} />
          <Route path="/clienthub/agreements" element={<AgreementsPage />} />
          <Route path="/workflows" element={<WorkflowsPage />} />
          <Route path="/workflows/:id" element={<WorkflowPage />} />
          <Route path="/reviewhub" element={<ReviewsPage />} />
          <Route path="/reviewhub/r/:id" element={<ReviewPage />} />
          <Route path="/vendorhub" element={<VendorsPage />} />
          <Route path="/vendorhub/v/:id" element={<VendorPage />} />
          <Route path="/new" element={<NewDocumentPage />} />
          <Route path="/new/:id" element={<NewDocumentPage />} />
          <Route path="/approvals" element={<ApprovalsPage />} />
          <Route path="/drafts" element={<DraftsPage />} />
          <Route path="/import" element={<ImportPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/help" element={<HelpHome />} />
          <Route path="/help/:id" element={<HelpArticlePage />} />
          <Route path="/setup" element={<KeepQuery to="/welcome" />} />
          {/* Settings, grouped by part (2026-10-23). Old addresses redirect. */}
          <Route path="/settings" element={<SettingsLayout />}>
            <Route path="companies" element={<CompaniesPage />} />
            <Route path="people" element={<MembersPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="profile" element={<KeepQuery to="/profile" />} />
            <Route path="approvals" element={<ApprovalSettingsPage />} />
            <Route path="codes" element={<CodeListsPage />} />
            <Route path="acronyms" element={<AcronymsPage />} />
            <Route path="publishing" element={<PublishKeysPage />} />
            <Route path="clienthub" element={<ClientHubAccessPage />} />
            <Route path="services" element={<ServicesSettingsPage />} />
            <Route path="agreement-types" element={<AgreementTypesPage />} />
            <Route path="integrations" element={<IntegrationsPage />} />
            <Route path="checklists" element={<ChecklistTemplatesPage />} />
            <Route path="workflows" element={<WorkflowTemplatesPage />} />
            <Route path="ai" element={<AiSettingsPage />} />
            <Route path="reviewhub" element={<ReviewHubAccessPage />} />
            <Route path="reviewhub/ratings" element={<RatingDefinitionsPage />} />
            <Route path="reviewhub/reminders" element={<RemindersPage />} />
            <Route path="reviewhub/page" element={<ClientPageSectionsPage />} />
            <Route path="vendorhub" element={<VendorHubAccessPage />} />
            <Route path="vendorhub/rules" element={<VendorRulesPage />} />
          </Route>
          <Route path="/settings/members" element={<KeepQuery to="/settings/people" />} />
          {/* My profile is its own page, not part of Settings (Bill, 2026-10-23). */}
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </StoreProvider>
  );
}
